import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { FaMapPin, FaCalendar, FaGlobe, FaTicketAlt, FaArrowLeft } from 'react-icons/fa';
import axiosPost from '../helpers/axios-helper';
import '../styles/Event.css';

export default function Event() {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const [eventId, setEventId] = useState(Number(queryParams.get('id')) || 0);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  useEffect(() => {
    // Resolve eventId from slug if no id provided
    if (!eventId && slug) {
      const controller = new AbortController();
      axiosPost(
        'events/list',
        { 'Content-Type': 'application/json' },
        { page: 1, per_page: 1000 },
        (resp) => {
          const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
          const found = items.find(it => slugify(it.title || '') === slug);
          if (found?.id) setEventId(Number(found.id));
        },
        controller
      );
      return () => controller.abort();
    }

    if (!eventId) return;

    const controller = new AbortController();
    setLoading(true);

    axiosPost(
      'events/get',
      { 'Content-Type': 'application/json' },
      { id: eventId },
      (resp) => {
        if (resp?.success && resp?.data) {
          setEvent(resp.data);
          setError(null);
        } else {
          setError('Event not found');
        }
        setLoading(false);
      },
      controller
    );

    return () => controller.abort();
  }, [eventId, slug]);

  // Redirect to slug-based URL if using id query param
  useEffect(() => {
    if (!event) return;
    const hasId = queryParams.get('id');
    if (hasId && !slug) {
      const s = slugify(event.title || '');
      if (s) navigate(`/event/${s}`, { replace: true });
    }
  }, [event, location.search, slug, navigate]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDateRange = (startTimestamp, endTimestamp) => {
    const startDate = formatDate(startTimestamp);
    const startTime = formatTime(startTimestamp);
    
    if (!endTimestamp) {
      return `${startDate} at ${startTime}`;
    }

    const endDate = formatDate(endTimestamp);
    const endTime = formatTime(endTimestamp);
    
    if (startDate === endDate) {
      return `${startDate}, ${startTime} - ${endTime}`;
    }
    
    return `${startDate} - ${endDate}`;
  };

  const getEventTypeBadge = (type) => {
    const badges = {
      'lgbtq': { label: 'LGBTQ+ Event', color: 'lgbtq' },
      'cultural': { label: 'Cultural', color: 'cultural' },
      'networking': { label: 'Networking', color: 'networking' },
      'workshop': { label: 'Workshop', color: 'workshop' },
      'party': { label: 'Party', color: 'party' },
      'festival': { label: 'Festival', color: 'festival' },
      'other': { label: 'Event', color: 'other' }
    };
    return badges[type] || badges.other;
  };

  if (loading) {
    return (
      <div className='event-loading'>
        <div className='loading-message'>Loading event...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className='event-error'>
        <div className='error-message'>{error || 'Event not found'}</div>
        <button className='btn-back' onClick={() => navigate(-1)}>
          <FaArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  const badge = getEventTypeBadge(event.event_type);
  const isLgbtq = event.event_type === 'lgbtq';

  return (
    <div className='event-page'>
      {/* Hero Section */}
      <div 
        className='event-hero'
        style={event.image_url ? {
          backgroundImage: `url(${event.image_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}}
      >
        <div className='event-hero-overlay'>
          <div className='event-hero-content'>
            <button className='btn-back-hero' onClick={() => navigate(-1)}>
              <FaArrowLeft /> Back
            </button>
            
            <div className='event-type-badge-container'>
              <span className={`event-type-badge badge-${badge.color}`}>
                {badge.label}
              </span>
              {isLgbtq && (
                <span className='lgbtq-badge'>
                  <span className='rainbow-circle'></span>
                  Pride Event
                </span>
              )}
            </div>

            <h1 className='event-hero-title'>{event.title}</h1>
            
            {event.location && (
              <div className='event-hero-location'>
                <FaMapPin /> {event.location}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Details */}
      <div className='event-details-container'>
        <div className='event-details'>
          {/* Date & Time Section */}
          <div className='event-info-card'>
            <div className='info-card-icon'>
              <FaCalendar size={24} />
            </div>
            <div className='info-card-content'>
              <h3>When</h3>
              <p className='event-date-range'>
                {formatDateRange(event.event_date, event.end_date)}
              </p>
            </div>
          </div>

          {/* Location Section */}
          {event.location && (
            <div className='event-info-card'>
              <div className='info-card-icon'>
                <FaMapPin size={24} />
              </div>
              <div className='info-card-content'>
                <h3>Where</h3>
                <p>{event.location}</p>
              </div>
            </div>
          )}

          {/* Description Section */}
          {event.description && (
            <div className='event-description-section'>
              <h2>About This Event</h2>
              <div className='event-description-text'>
                {event.description}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className='event-actions-section'>
            {event.ticket_url && (
              <a 
                href={event.ticket_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className='event-action-btn btn-primary'
              >
                <FaTicketAlt /> Get Tickets
              </a>
            )}
            {event.website_url && (
              <a 
                href={event.website_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className='event-action-btn btn-secondary'
              >
                <FaGlobe /> Visit Website
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
